import { useApiBase } from './useApiBase'

export interface GithubAuthStartResponse {
  authorizationUrl: string
  connected: boolean
  githubUsername: string | null
}

export interface GithubAuthCallbackResponse {
  connected: boolean
  githubUsername: string
}

export interface GithubAuthDisconnectResponse {
  disconnected: boolean
}

// --- Repository listing (Step 2) ---

export interface GithubRepo {
  name: string
  fullName: string
  description: string | null
  htmlUrl: string
  private: boolean
  defaultBranch: string
  updatedAt: string
}

export interface ListGithubReposResponse {
  repos: GithubRepo[]
}

export interface GithubTreeEntry {
  path: string
  size: number | null
}

export interface GetGithubRepoTreeResponse {
  files: GithubTreeEntry[]
  defaultBranch: string
  totalFiles: number
  /**
   * #764: 取り込み対象ファイルの合計サイズ。除外ルール（`.git/` や `node_modules/`）を
   * 適用したあとの値なので、実際に取り込まれる量と対応する。Trees API が blob ごとの
   * size を返すので**追加のダウンロードは発生しない**。
   */
  totalSizeBytes?: number
  /** 巨大リポでは Trees API が結果を打ち切る。そのとき合計は下限でしかない。 */
  truncated?: boolean
}

export interface GetGithubRepoReadmeResponse {
  readme: string | null
}

/**
 * GitHub API composable — OAuth + repository listing.
 */
export function useGithubApi() {
  const { apiBase, authenticatedFetch } = useApiBase()

  const getGithubAuthUrl = () => {
    return authenticatedFetch<GithubAuthStartResponse>(
      `${apiBase}/api/v1/auth/github`,
    )
  }

  const exchangeGithubCode = (code: string, state: string) => {
    return authenticatedFetch<GithubAuthCallbackResponse>(
      `${apiBase}/api/v1/auth/github/callback`,
      { method: 'POST', body: { code, state } },
    )
  }

  const disconnectGithub = () => {
    return authenticatedFetch<GithubAuthDisconnectResponse>(
      `${apiBase}/api/v1/auth/github`,
      { method: 'DELETE' },
    )
  }

  const listGithubRepos = () => {
    return authenticatedFetch<ListGithubReposResponse>(
      `${apiBase}/api/v1/github/repos`,
    )
  }

  const getGithubRepoTree = (owner: string, repo: string) => {
    return authenticatedFetch<GetGithubRepoTreeResponse>(
      `${apiBase}/api/v1/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/tree`,
    )
  }

  const getGithubRepoReadme = (owner: string, repo: string) => {
    return authenticatedFetch<GetGithubRepoReadmeResponse>(
      `${apiBase}/api/v1/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/readme`,
    )
  }

  const createGithubSession = (owner: string, repo: string, entrypoint: string, screening = false) => {
    return authenticatedFetch<{
      sessionId: string
      sessionUrl: string
      pyodidePackages?: string[]
      /** #764: ビルドされた pyxapp のサイズ。クリスタル化前の大きさ表示に使う。 */
      sizeBytes?: number | null
    }>(
      `${apiBase}/api/v1/sessions`,
      {
        method: 'POST',
        body: JSON.stringify({
          githubSource: { owner, repo, entrypoint },
          ...(screening ? { screening: true } : {}),
        }),
      },
    )
  }

  return {
    getGithubAuthUrl,
    exchangeGithubCode,
    disconnectGithub,
    listGithubRepos,
    getGithubRepoTree,
    getGithubRepoReadme,
    createGithubSession,
  }
}
